import { useEffect, useState } from 'react'

export interface PlanEntity {
  rank: number
  plan_id: number
  plan_name: string
  plan_type: string
  price: number | null
  credit_amount: number
  max_uses: number | null
  is_web_visible: boolean | null
  is_kiosk_visible: boolean | null
  units_sold: number
  revenue: number
  avg_price_paid: number | null
  kiosk_units: number | null
  online_units: number | null
  cashier_units: number | null
}

export interface AttractionEntity {
  rank: number
  attraction_id: number
  attraction_name: string
  attraction_type: string
  subcontractor: string | null
  is_active: boolean
  total_scans: number
  paid_scans: number
  unique_guests: number
  revenue: number
  repeat_guests: number | null
  repeat_play_pct: number | null
  peak_hour_scans: number | null
  avg_scans_per_hour: number | null
  active_hours: number | null
  performance: 'over' | 'average' | 'under' | null
  z_score: number | null
}

export interface EmployeeEntity {
  employee_id: number
  name: string
  email?: string
  role: string
  assigned_attraction?: string
  clover_serial?: string
  is_active: boolean
  total_scans: number
  revenue: number
  unique_bands: number
  active_hours: number
  scans_per_hour: number
}

export interface KioskEntity {
  rank: number
  kiosk_id: number
  kiosk_name: string
  kiosk_type: string
  kiosk_group: string | null
  placement_type: string | null
  orders: number
  revenue: number
  unique_bands: number
  new_band_orders: number
  reload_orders: number
  eticket_orders: number
  active_hours: number
  orders_per_hour: number
  avg_transaction_value: number
  card_pct: number | null
}

export interface EntityData {
  plans: PlanEntity[]
  attractions: AttractionEntity[]
  employees: {
    humans: EmployeeEntity[]
    kiosks: EmployeeEntity[]
    ride_scanners: EmployeeEntity[]
  }
  kiosks: KioskEntity[]
}

export interface EventMeta {
  event: { event_id: number; event_name: string; event_date: string }
}

export function useEntityData(eventId: number | null) {
  const [data, setData] = useState<EntityData | null>(null)
  const [eventMeta, setEventMeta] = useState<EventMeta | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!eventId || !Number.isFinite(eventId)) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)

    Promise.all([
      fetch(`/api/dashboard/${eventId}/entities`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? (r.json() as Promise<EntityData>) : null)),
      fetch(`/api/dashboard/${eventId}/stats`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? (r.json() as Promise<EventMeta>) : null)),
    ]).then(([entities, stats]) => {
      if (cancelled) return
      setData(entities ?? null)
      setEventMeta(stats ?? null)
      setLoading(false)
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [eventId])

  return { data, eventMeta, loading }
}
